import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { ProductAttribute } from '@/types/application/inventory-management/ProductV2DTO';
import { useMemo } from 'react';
import { useGT } from 'gt-react';
import { Label } from '../ui/label';

export function CoreEnumAutocomplete({
  attribute,
  onAttributeChange,
  selectedAttributes,
  selectedAttributeOptionsByAttrId,
  onEnumSelectionByAttrId,
  disabled,
}: {
  attribute: ProductAttribute;
  onAttributeChange: (originEntity: string, originId: string | null) => void;
  selectedAttributes: Record<string, string | null>;
  selectedAttributeOptionsByAttrId?: Record<string, string>;
  onEnumSelectionByAttrId?: (
    productAttributeId: string,
    optionId: string | null,
  ) => void;
  disabled?: boolean;
}) {
  const gt = useGT();
  const sortedOptions = useMemo(
    () =>
      (attribute.customField.coreEnum?.options || []).toSorted((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      ),
    [attribute.customField.coreEnum?.options],
  );

  const currentValue = useMemo(() => {
    // If we have the new dual state tracking, use selectedAttributeOptionsByAttrId
    if (selectedAttributeOptionsByAttrId && onEnumSelectionByAttrId) {
      return selectedAttributeOptionsByAttrId[attribute.id];
    }

    // Fallback to the original logic
    return attribute.customField.coreEnum?.options.find(
      (opt) =>
        opt.originId ===
        selectedAttributes[attribute.customField.coreEnum?.originEntity || ''],
    )?.id;
  }, [
    selectedAttributeOptionsByAttrId,
    selectedAttributes,
    attribute,
    onEnumSelectionByAttrId,
  ]);

  return (
    <Label className="flex flex-col gap-1 w-full items-start">
      <span className="text-sm font-medium">
        {attribute.customField.fieldName}
        {attribute.isRequired && <span className="text-red-500">*</span>}
      </span>
      <Autocomplete
        items={sortedOptions}
        getValue={(option) => option.id}
        getLabel={(option) => option.name || ''}
        value={currentValue || null}
        onValueChange={(attributeId) => {
          // When value is falsey, it means the field was cleared
          if (!attributeId) {
            // Update both state systems if available
            if (onEnumSelectionByAttrId) {
              onEnumSelectionByAttrId(attribute.id, null);
            }

            // Always update the origin-based selection for variant matching
            if (
              attribute.customField.coreEnum &&
              attribute.customField.coreEnum.originEntity
            ) {
              onAttributeChange(
                attribute.customField.coreEnum.originEntity,
                null,
              );
            }
          } else {
            const option = sortedOptions.find((opt) => opt.id === attributeId);
            if (option) {
              // Update both state systems if available
              if (onEnumSelectionByAttrId) {
                onEnumSelectionByAttrId(attribute.id, attributeId);
              }

              // Always update the origin-based selection for variant matching
              onAttributeChange(
                option.originEntity || '',
                option.originId || '',
              );
            }
          }
        }}
        labels={pickerLabels({
          placeholder: attribute.customField.fieldName,
          search: gt('Search options...', {
            context: 'search box placeholder inside a picker dropdown',
          }),
        })}
        deselectable
        allowClear
        disabled={disabled}
      />
      {attribute.customField.description && (
        <span className="text-xs text-muted-foreground">
          {attribute.customField.description}
        </span>
      )}
    </Label>
  );
}
